import React, { useEffect, useState, useMemo } from 'react';
import { Button, Drawer } from 'components/common';
import { Controller, useForm } from "react-hook-form";
import { useSelector, useDispatch } from "react-redux";
import { MdOutlineClose, MdOutlineSave } from 'react-icons/md';
import DatePicker from "react-datepicker";
import { adjustToNoon } from 'helpers/utils';
import Select from 'react-select'
import { updateRequestTerminalAccount } from "redux/request/terminalAccountSlice";
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import { getDepartmentBySite, getActiveSites } from "redux/organization/siteSlice";

import { INPUT_CLS as INPUT_CLASSES } from 'helpers/formStyles';
const EditRequestTerminalAccountForm = ({ open, setOpen, record }) => {
  const { control, register, handleSubmit, formState: { errors }, setValue, reset } = useForm({
    defaultValues: {
      purpose: []
    },
    mode: 'onChange',
  });

  const { data: user } = useSelector(state => state.profile_user);
  const { activeData: siteData } = useSelector(state => state.organization_site);
  const dispatch = useDispatch();

  const [selectedSite, setSelectedSite] = useState(null);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);

  useEffect(() => {
    if (record) {
      // Set selected site from record or user
      const siteId = record?.site?.id || user?.site?.id;
      setSelectedSite(siteId);

      Object.entries(record).forEach(([key, value]) => {
        switch (key) {
          case "terminal_date":
            setValue(key, value ? new Date(value) : null);
            break;
          case "department":
            setValue("department_id", value ? value.id : null);
            break;
          case "site":
            setValue("site_id", value ? value.id : user?.site?.id);
            break;
          default:
            setValue(key, value);
            break;
        }
      })
    } else {
      reset({ purpose: [] });
      setSelectedSite(user?.site?.id);
    }
  }, [setValue, record, open, reset, user]);

  useEffect(() => {
    if (!siteData || siteData.length === 0) {
      dispatch(getActiveSites());
    }
  }, [dispatch, siteData]);

  useEffect(() => {
    if (selectedSite) {
      fetchDepartments(selectedSite);
    }
    // Refetch only when the site changes; fetchDepartments reads the current record's department.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSite]);


  const fetchDepartments = async (siteId) => {
    if (!siteId) return;

    setLoadingDepartments(true);
    try {
      const response = await dispatch(getDepartmentBySite(siteId)).unwrap();

      const options = response.rows.map(item => item.attributes).map(item => ({
        value: item.id,
        label: item.name
      }));

      // If record has a department, ensure it's in the options
      if (record?.department) {
        const recordDeptExists = options.some(opt => opt.value === record.department.id);
        if (!recordDeptExists) {
          options.unshift({
            value: record.department.id,
            label: record.department.name
          });
        }
      }

      setDepartmentOptions(options);

    } catch (error) {
      toast.error(error.message);
      setDepartmentOptions([]);
    } finally {
      setLoadingDepartments(false);
    }
  };

  const handleSiteChange = (selectedOption) => {
    const newSiteId = selectedOption?.value;
    setSelectedSite(newSiteId);
    setValue('site_id', newSiteId, { shouldValidate: true });
    // Reset department when site changes
    setValue('department_id', null);
  };

  const siteOptions = useMemo(() =>
    siteData && siteData.map((site) => ({ value: site?.id, label: site?.name })),
    [siteData]
  );

  const defaultSiteOption = useMemo(() => ({
    value: record?.site?.id || user?.site?.id,
    label: record?.site?.name || user?.site?.name
  }), [record, user]);

  const closeModal = () => {
    setOpen(false);
  }

  const onSubmit = async (data) => {
    try {
      await dispatch(updateRequestTerminalAccount({ recordId: record?.id, data }));
      closeModal();
    }
    catch (error) {
      toast.error(error.message)
      return;
    }
  }

  const fieldRow = (label, children) => (
    <div className="tw-grid tw-grid-cols-12 tw-gap-3 tw-mb-2 tw-items-center">
      <div className="tw-col-span-12 sm:tw-col-span-3 tw-text-sm tw-font-medium tw-text-slate-700 sm:tw-text-right">
        {label}
      </div>
      <div className="tw-col-span-12 sm:tw-col-span-7">
        {children}
      </div>
    </div>
  );

  const fieldError = (error) => error && (
    <span className="tw-text-red-600 tw-text-xs tw-mt-1 tw-block">{error.message}</span>
  );

  return (
    <Drawer
      show={open}
      onHide={closeModal}
      title="Edit Request User Account"
      width="50%"
      backdropClick={false}
      footer={(
        <>
          <Button size="sm" type="submit" variant="primary" className="tw-mr-3" form="edit-request-terminal-account-form">
            <MdOutlineSave /> Update
          </Button>
          <Button size="sm" variant="danger" onClick={closeModal}>
            <MdOutlineClose /> Close
          </Button>
        </>
      )}
    >
      <form id="edit-request-terminal-account-form" onSubmit={handleSubmit(onSubmit)}>
        {fieldRow('Name*', (
          <>
            <input
              type='text'
              placeholder='Full of name or 3 characters as policy'
              className={INPUT_CLASSES}
              {...register('name', {
                required: 'Name must be required.'
              })}
            />
            {fieldError(errors.name)}
          </>
        ))}

        {fieldRow('Email*', (
          <>
            <input
              type='email'
              placeholder='Email'
              className={INPUT_CLASSES}
              {...register('email', {
                required: 'Email must be required.',
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Email is not valid.'
                }
              })}
            />
            {fieldError(errors.email)}
          </>
        ))}

        {fieldRow('Title', (
          <>
            <input
              type='text'
              placeholder='Title'
              className={INPUT_CLASSES}
              {...register('title', {
                required: 'Title must be required.'
              })}
            />
            {fieldError(errors.title)}
          </>
        ))}

        {fieldRow('Terminal date*', (
          <>
            <Controller
              control={control}
              name="terminal_date"
              rules={{ required: 'Terminal date must be required.' }}
              render={({ field }) => (
                <DatePicker
                  selected={field.value}
                  onChange={d => field.onChange(adjustToNoon(d))}
                  className={INPUT_CLASSES}
                  placeholderText="DD-MM-YYYY"
                  dateFormat="dd-MM-yyyy"
                  fixedHeight
                  isClearable={true}
                />
              )}
            />
            {fieldError(errors.terminal_date)}
          </>
        ))}

        {fieldRow(<>Site office<span className="tw-text-blue-600">*</span></>, (
          <>
            <Controller
              control={control}
              name="site_id"
              render={({ field }) => (
                <Select
                  closeMenuOnSelect={true}
                  options={siteOptions}
                  value={siteOptions?.find(opt => opt.value === field.value) || defaultSiteOption}
                  placeholder='Select...'
                  classNamePrefix="react-select"
                  onChange={handleSiteChange}
                />
              )}
              rules={{
                required: {
                  value: true,
                  message: 'Site must be required'
                }
              }}
            />
            {fieldError(errors.site_id)}
          </>
        ))}

        {fieldRow('Department', (
          <Controller
            control={control}
            name='department_id'
            render={({ field }) => (
              <Select
                isClearable
                options={departmentOptions}
                value={departmentOptions && departmentOptions.find(opt => opt.value === field.value)}
                onChange={selectedOption => {
                  field.onChange(selectedOption?.value);
                }}
                placeholder={loadingDepartments ? 'Loading departments...' : 'Select...'}
                classNamePrefix='react-select'
                className='tw-w-full'
                isLoading={loadingDepartments}
                isDisabled={loadingDepartments}
              />
            )}
          />
        ))}

        {fieldRow('Remark', (
          <textarea
            rows={10}
            placeholder="Note in special cases, keep the email for a period of time."
            className={INPUT_CLASSES}
            {...register("remark")}
          />
        ))}
      </form>
    </Drawer>
  )
}

EditRequestTerminalAccountForm.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  record: PropTypes.object
};

export default EditRequestTerminalAccountForm;
